import { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Card,
  CardActions,
  CardContent,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  createRule,
  listConnections,
  listCalendars,
  refreshCalendars,
} from '@/api/endpoints';
import type { CalendarOut, Connection, SyncRule } from '@/api/types';
import { ruleDefaults, ruleSchema, type RuleFormValues } from './ruleSchema';

type WizardMode = 'standalone' | 'onboarding';

interface Props {
  mode?: WizardMode;
  onCreated?: (rule: SyncRule) => void;
  onCancel?: () => void;
}

const STEPS = ['Source', 'Target', 'Mirror settings', 'Review'];

/**
 * Four-step rule wizard with progressive form validation. Each "Next" button
 * triggers validation only for the fields owned by the current step; the
 * final "Save" runs the full schema before POSTing.
 */
export function RuleWizard({ mode = 'standalone', onCreated, onCancel }: Props) {
  const [step, setStep] = useState(0);
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const form = useForm<RuleFormValues>({
    resolver: zodResolver(ruleSchema),
    defaultValues: ruleDefaults,
    mode: 'onBlur',
  });

  const connections = useQuery<Connection[]>({
    queryKey: ['connections'],
    queryFn: listConnections,
  });

  const create = useMutation({
    mutationFn: (values: RuleFormValues) => createRule(values),
    onSuccess: (rule) => {
      enqueueSnackbar(`Rule "${rule.name}" created.`, { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['rules'] });
      onCreated?.(rule);
    },
    onError: (err) => enqueueSnackbar(err.message, { variant: 'error' }),
  });

  const validateStep = async (): Promise<boolean> => {
    const fields: Record<number, (keyof RuleFormValues)[]> = {
      0: ['source_calendar_id'],
      1: ['target_calendar_id'],
      2: ['name', 'replacement_title', 'min_duration_minutes', 'detection_mode', 'poll_interval_s'],
      3: [],
    };
    return form.trigger(fields[step] ?? []);
  };

  const onCreate = async () => {
    // Trigger full-form validation then submit if it passes. Side-steps any
    // accidental form submissions from button clicks elsewhere in the tree.
    const ok = await form.trigger();
    if (!ok) return;
    create.mutate(form.getValues());
  };

  if (connections.isLoading) {
    return (
      <Stack alignItems="center" sx={{ py: 6 }}>
        <CircularProgress size={28} />
      </Stack>
    );
  }

  return (
    <Card variant="outlined">
      <CardContent>
        <Stepper activeStep={step} sx={{ mb: 3 }} alternativeLabel>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {step === 0 && (
          <CalendarPicker
            label="Source calendar"
            description="Events from this calendar will be mirrored."
            connections={connections.data ?? []}
            value={form.watch('source_calendar_id')}
            onChange={(v) => form.setValue('source_calendar_id', v, { shouldValidate: true })}
            error={form.formState.errors.source_calendar_id?.message}
            excludeCalendarId={form.watch('target_calendar_id') || undefined}
          />
        )}

        {step === 1 && (
          <CalendarPicker
            label="Target calendar"
            description="Mirrored busy blocks will be written here."
            connections={connections.data ?? []}
            value={form.watch('target_calendar_id')}
            onChange={(v) => form.setValue('target_calendar_id', v, { shouldValidate: true })}
            error={form.formState.errors.target_calendar_id?.message}
            excludeCalendarId={form.watch('source_calendar_id') || undefined}
          />
        )}

        {step === 2 && <MirrorSettingsStep form={form} />}

        {step === 3 && <ReviewStep values={form.getValues()} />}
      </CardContent>
      <Divider />
      <CardActions sx={{ justifyContent: 'space-between', px: 2, py: 1.5 }}>
        <Button
          type="button"
          onClick={step === 0 ? onCancel : () => setStep((s) => Math.max(0, s - 1))}
        >
          {step === 0 ? (mode === 'onboarding' ? 'Back' : 'Cancel') : 'Back'}
        </Button>
        {step < STEPS.length - 1 ? (
          // Explicit type="button" — without this, the default `type` inside
          // a <form> is "submit", which would create the rule the moment
          // validation passes (i.e. on the last pre-review step).
          <Button
            type="button"
            variant="contained"
            onClick={async () => (await validateStep()) && setStep((s) => s + 1)}
          >
            Next
          </Button>
        ) : (
          <Button
            type="button"
            variant="contained"
            onClick={onCreate}
            disabled={create.isPending}
          >
            {create.isPending ? 'Creating…' : 'Create rule'}
          </Button>
        )}
      </CardActions>
    </Card>
  );
}

interface CalendarPickerProps {
  label: string;
  description: string;
  connections: Connection[];
  value: number;
  onChange: (v: number) => void;
  error?: string;
  excludeCalendarId?: number;
}

function CalendarPicker({
  label,
  description,
  connections,
  value,
  onChange,
  error,
  excludeCalendarId,
}: CalendarPickerProps) {
  const [connectionId, setConnectionId] = useState<number | ''>(connections[0]?.id ?? '');
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const calendars = useQuery<CalendarOut[]>({
    queryKey: ['calendars', connectionId],
    queryFn: () => (connectionId === '' ? Promise.resolve([]) : listCalendars(connectionId)),
    enabled: connectionId !== '',
  });

  const refresh = useMutation({
    mutationFn: (id: number) => refreshCalendars(id),
    onSuccess: (data) => {
      queryClient.setQueryData(['calendars', connectionId], data);
      enqueueSnackbar('Calendar list refreshed.', { variant: 'success' });
    },
    onError: (err) => enqueueSnackbar(err.message, { variant: 'error' }),
  });

  const filtered = useMemo(
    () =>
      (calendars.data ?? []).filter(
        (c) => excludeCalendarId === undefined || c.id !== excludeCalendarId,
      ),
    [calendars.data, excludeCalendarId],
  );

  return (
    <Stack spacing={2}>
      <Typography variant="h6">{label}</Typography>
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>

      <FormControl fullWidth>
        <InputLabel>Connection</InputLabel>
        <Select
          label="Connection"
          value={connectionId}
          onChange={(e) => setConnectionId(Number(e.target.value))}
        >
          {connections.map((c) => (
            <MenuItem key={c.id} value={c.id}>
              {c.provider_account_label} — {c.provider_display_name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Stack direction="row" alignItems="center" spacing={1}>
        <FormControl fullWidth error={!!error}>
          <InputLabel>Calendar</InputLabel>
          <Select
            label="Calendar"
            value={value || ''}
            onChange={(e) => onChange(Number(e.target.value))}
            disabled={calendars.isLoading || filtered.length === 0}
          >
            {filtered.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name}
                {c.is_primary_default && (
                  <Typography
                    component="span"
                    variant="caption"
                    color="text.secondary"
                    sx={{ ml: 1 }}
                  >
                    (primary)
                  </Typography>
                )}
              </MenuItem>
            ))}
            {!calendars.isLoading && filtered.length === 0 && (
              <MenuItem disabled value="">
                No calendars available
              </MenuItem>
            )}
          </Select>
          {error && <FormHelperText>{error}</FormHelperText>}
        </FormControl>
        <Tooltip title="Refresh calendar list from provider">
          <span>
            <IconButton
              onClick={() => connectionId !== '' && refresh.mutate(connectionId)}
              disabled={connectionId === '' || refresh.isPending}
              aria-label="Refresh calendars"
            >
              {refresh.isPending ? <CircularProgress size={20} /> : <RefreshIcon />}
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    </Stack>
  );
}

function MirrorSettingsStep({
  form,
}: {
  form: ReturnType<typeof useForm<RuleFormValues>>;
}) {
  const { control, register, watch, formState } = form;
  const detectionMode = watch('detection_mode');
  return (
    <Stack spacing={2.5}>
      <Typography variant="h6">Mirror settings</Typography>
      <TextField
        label="Rule name"
        fullWidth
        {...register('name')}
        error={!!formState.errors.name}
        helperText={formState.errors.name?.message ?? 'A label just for you.'}
      />
      <TextField
        label="Replacement title"
        fullWidth
        {...register('replacement_title')}
        error={!!formState.errors.replacement_title}
        helperText={
          formState.errors.replacement_title?.message ??
          'Every mirrored event will be retitled to this.'
        }
      />
      <TextField
        label="Minimum duration (minutes)"
        type="number"
        fullWidth
        {...register('min_duration_minutes', { valueAsNumber: true })}
        error={!!formState.errors.min_duration_minutes}
        helperText={
          formState.errors.min_duration_minutes?.message ?? 'Shorter events will be skipped.'
        }
      />
      <Controller
        name="allow_downstream_overrides"
        control={control}
        render={({ field }) => (
          <FormControlLabel
            control={<Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />}
            label="Allow downstream overrides"
          />
        )}
      />
      <Typography variant="caption" color="text.secondary" sx={{ mt: -2 }}>
        When enabled, manual edits on a mirrored event in the target calendar are preserved on the next sync.
      </Typography>

      <Accordion variant="outlined" disableGutters>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">Advanced</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <Controller
              name="detection_mode"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel>Detection mode</InputLabel>
                  <Select label="Detection mode" {...field}>
                    <MenuItem value="webhook">Webhook (provider push)</MenuItem>
                    <MenuItem value="poll">Polling</MenuItem>
                  </Select>
                  <FormHelperText>
                    Webhook is preferred. Cadence falls back to polling automatically if a subscription expires.
                  </FormHelperText>
                </FormControl>
              )}
            />
            <TextField
              label="Poll interval (seconds)"
              type="number"
              fullWidth
              disabled={detectionMode !== 'poll'}
              {...register('poll_interval_s', { valueAsNumber: true })}
              error={!!formState.errors.poll_interval_s}
              helperText={
                formState.errors.poll_interval_s?.message ??
                'Only used when detection mode is Polling or webhook is unhealthy.'
              }
            />
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Stack>
  );
}

function ReviewStep({ values }: { values: RuleFormValues }) {
  const calNames = useCalendarNames([values.source_calendar_id, values.target_calendar_id]);
  return (
    <Stack spacing={1.5}>
      <Typography variant="h6">Review</Typography>
      <ReviewRow label="Name" value={values.name} />
      <ReviewRow label="Source calendar" value={calNames[values.source_calendar_id] ?? '—'} />
      <ReviewRow label="Target calendar" value={calNames[values.target_calendar_id] ?? '—'} />
      <ReviewRow label="Replacement title" value={values.replacement_title} />
      <ReviewRow
        label="Minimum duration"
        value={`${values.min_duration_minutes} min`}
      />
      <ReviewRow
        label="Allow downstream overrides"
        value={values.allow_downstream_overrides ? 'Yes' : 'No'}
      />
      <ReviewRow label="Detection mode" value={values.detection_mode} />
      {values.detection_mode === 'poll' && (
        <ReviewRow label="Poll interval" value={`${values.poll_interval_s}s`} />
      )}
    </Stack>
  );
}

function ReviewRow({ label, value }: { label: string; value: string }) {
  return (
    <Stack direction="row" justifyContent="space-between" sx={{ py: 0.5 }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </Stack>
  );
}

function useCalendarNames(ids: number[]): Record<number, string> {
  // Look across every cached calendars query to resolve the picked ids to
  // display names without firing a new request.
  const queryClient = useQueryClient();
  const cache = queryClient.getQueryCache();
  const result: Record<number, string> = {};
  for (const entry of cache.findAll({ queryKey: ['calendars'] })) {
    const data = entry.state.data as CalendarOut[] | undefined;
    if (!data) continue;
    for (const c of data) if (ids.includes(c.id)) result[c.id] = c.name;
  }
  return result;
}
